/* Waternex PDP - below-fold builder.
   Turns the product description into the designed block layout: key-facts strip,
   tick lists, bordered spec tables, a What's Included panel, notes callouts and
   download cards. Falls back to the plain (styled) description if a description
   has no recognisable headings, so it can never strand content. */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  var TICK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M20 6L9 17l-5-5"/></svg>';
  var DOWN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 4v13M6 12l6 6 6-6"/></svg>';

  function slug(s) {
    return 'wnx-s-' + s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 36);
  }

  function splitKV(text) {
    var i = text.indexOf(':');
    if (i < 1 || i > 46) return null;
    var k = text.slice(0, i).trim(), v = text.slice(i + 1).trim();
    if (!k || !v || v.length > 90) return null;
    return [k, v];
  }

  /* lift children out of wrapper divs so headings sit at the top level */
  function flatten(el) {
    for (var i = 0; i < 5; i++) {
      var changed = false;
      Array.prototype.slice.call(el.children).forEach(function (c) {
        if (!/^(DIV|SECTION|ARTICLE)$/.test(c.tagName)) return;
        if (!c.querySelector('h1,h2,h3,h4,h5,h6')) return;
        while (c.firstChild) el.insertBefore(c.firstChild, c);
        el.removeChild(c);
        changed = true;
      });
      if (!changed) break;
    }
    return el;
  }

  /* a lone bold paragraph is a heading in older descriptions */
  function promoteBold(el) {
    Array.prototype.slice.call(el.querySelectorAll('p')).forEach(function (p) {
      var t = (p.textContent || '').trim();
      if (!t || t.length > 70 || p.querySelector('a,img')) return;
      var s = p.querySelector('strong,b');
      if (!s || s.textContent.trim().length < t.length * 0.85) return;
      var h = document.createElement('h3');
      h.textContent = t;
      p.parentNode.replaceChild(h, p);
    });
    return el;
  }

  var FACTS = [
    { re: /nominal volume|tank capacity|^capacity$|^volume$/i, label: 'Capacity', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12v3H6zM7 6l1 15h8l1-15"/></svg>' },
    { re: /^heating capacity/i, label: 'Heating', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2s5 5 5 9a5 5 0 0 1-10 0c0-4 5-9 5-9z"/></svg>' },
    { re: /^cooling\s*:/i, label: 'Cools pool too', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v20M4 6l16 12M20 6L4 18"/></svg>' },
    { re: /^wi[- ]?fi\s*:/i, label: 'WiFi', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M2 8.5C6 5 10 3.5 12 3.5s6 1.5 10 5M5 12c3-2.5 5-3.5 7-3.5s4 1 7 3.5M8.5 15.5c1.5-1.2 2.5-1.8 3.5-1.8s2 .6 3.5 1.8"/><circle cx="12" cy="19" r="1.3" fill="currentColor" stroke="none"/></svg>' },
    { re: /^power$|motor power|rated power|max power input/i, label: 'Max power', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L4 14h7l-1 8 9-12h-7z"/></svg>' },
    { re: /max(imum)? head|^head$/i, label: 'Max head', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M5 10l7-7 7 7"/></svg>' },
    { re: /max(imum)? flow|flow rate/i, label: 'Max flow', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12c3-4 6-4 9 0s6 4 9 0M3 18c3-4 6-4 9 0s6 4 9 0"/></svg>' },
    { re: /warranty/i, label: 'Warranty', icon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l8 3v6c0 5-4 9-8 11-4-2-8-6-8-11V5z"/></svg>' }
  ];

  function build(root) {
    var desc = root.querySelector('.wnx-desc');
    if (!desc || desc.dataset.built) return;
    desc.dataset.built = '1';

    promoteBold(flatten(desc));

    /* ---- split into sections on headings ---- */
    var intro = [], secs = [], cur = null;
    Array.prototype.slice.call(desc.childNodes).forEach(function (n) {
      var isH = n.nodeType === 1 && /^H[1-6]$/.test(n.tagName);
      if (isH && !(n.textContent || '').trim()) return;      /* spacer heading */
      if (isH) { cur = { title: n.textContent.trim(), nodes: [] }; secs.push(cur); }
      else if (cur) cur.nodes.push(n);
      else if (n.nodeType === 1 || (n.textContent || '').trim()) intro.push(n);
    });

    var downloads = [];
    var harvestPdfs = function (el) {
      Array.prototype.forEach.call(el.querySelectorAll('a[href*=".pdf"]'), function (a) {
        downloads.push(a.cloneNode(true));
      });
    };

    /* no headings at all: keep the plain styled description, just grab any PDFs */
    if (!secs.length) { harvestPdfs(desc); renderDownloads(root, downloads); wireNav(root, desc); return; }

    var out = document.createElement('div');
    out.className = 'wnx-secs';

    if (intro.length) {
      var lede = document.createElement('div');
      lede.className = 'wnx-lede';
      intro.forEach(function (n) { lede.appendChild(n.cloneNode(true)); });
      out.appendChild(lede);
    }

    var facts = [], inclEl = null;

    secs.forEach(function (s) {
      var low = s.title.toLowerCase();
      var body = document.createElement('div');
      body.className = 'wnx-sec-b';
      s.nodes.forEach(function (n) { body.appendChild(n.cloneNode(true)); });
      harvestPdfs(body);

      /* these are rendered by our own blocks further down */
      if (/^downloads?$|^documents?$|^brochures?$/i.test(low.trim())) return;
      if (/have questions|need assistance|ready to order|contact us|pump station range/i.test(low)) return;

      var isSpec  = /specification|technical|performance|dimension|warranty/i.test(low);
      var isIncl  = /what.{0,3}s included|in the box|package includes|what you get/i.test(low);
      var isNote  = /important|please note|warning|disclaimer/i.test(low);
      var isHigh  = /highlight|feature|benefit/i.test(low);

      /* harvest key facts from any "Label: value" line */
      Array.prototype.forEach.call(body.querySelectorAll('li'), function (li) {
        var kv = splitKV(li.textContent.replace(/\s+/g, ' ').trim());
        if (!kv) return;
        FACTS.forEach(function (f) {
          if (!f.re.test(kv[0])) return;
          if (facts.some(function (x) { return x.label === f.label; })) return;
          facts.push({ label: f.label, value: kv[1].split('(')[0].trim().replace(/\.$/, ''), icon: f.icon });
        });
      });

      if (isSpec) {
        Array.prototype.forEach.call(body.querySelectorAll('ul,ol'), function (ul) {
          var rows = [];
          Array.prototype.forEach.call(ul.querySelectorAll('li'), function (li) {
            var kv = splitKV(li.textContent.replace(/\s+/g, ' ').trim());
            if (kv) rows.push(kv);
          });
          if (rows.length >= 2 && rows.length >= ul.querySelectorAll('li').length * 0.6) {
            var w = document.createElement('div');
            w.className = 'wnx-spectable';
            w.innerHTML = '<table><tbody>' + rows.map(function (r) {
              return '<tr><th>' + r[0] + '</th><td>' + r[1] + '</td></tr>';
            }).join('') + '</tbody></table>';
            ul.parentNode.replaceChild(w, ul);
          }
        });
      }

      if (isIncl) {
        Array.prototype.forEach.call(body.querySelectorAll('ul,ol'), function (ul) {
          var items = Array.prototype.map.call(ul.querySelectorAll('li'), function (li) {
            var html = li.innerHTML.trim();
            var m = li.textContent.trim().match(/^(\d+)\s*[x×X]\s*/);
            var chip = m ? m[1] + '&times;' : '<span class="wnx-inc-tick">' + TICK + '</span>';
            if (m) html = html.replace(/^\s*\d+\s*[x×X]\s*/, '');
            return '<li><span class="wnx-inc-n">' + chip + '</span><span>' + html + '</span></li>';
          });
          var panel = document.createElement('div');
          panel.className = 'wnx-panel';
          panel.innerHTML = '<ul class="wnx-inc">' + items.join('') + '</ul>';
          ul.parentNode.replaceChild(panel, ul);
        });
      }

      if (isHigh) {
        Array.prototype.forEach.call(body.querySelectorAll('ul'), function (ul) {
          ul.classList.add('wnx-ticks');
          Array.prototype.forEach.call(ul.querySelectorAll('li'), function (li) {
            li.innerHTML = '<span class="wnx-tick">' + TICK + '</span><span>' + li.innerHTML + '</span>';
          });
        });
      }

      var sec = document.createElement('section');
      sec.className = 'wnx-sec' + (isNote ? ' wnx-note' : '');
      sec.id = slug(s.title);
      var h = document.createElement('h3');
      h.className = 'wnx-sec-h';
      h.textContent = s.title;
      sec.appendChild(h);
      sec.appendChild(body);
      out.appendChild(sec);

      if (isIncl && !inclEl) inclEl = sec;
    });

    /* key facts strip above everything */
    var kf = root.querySelector('.wnx-keyfacts');
    if (kf && facts.length >= 3) {
      kf.innerHTML = facts.slice(0, 4).map(function (f) {
        return '<div class="wnx-kf">' + (f.icon ? '<div class="wnx-kf-i">' + f.icon + '</div>' : '') + '<div class="wnx-kf-v">' + f.value + '</div><div class="wnx-kf-l">' + f.label + '</div></div>';
      }).join('');
      kf.hidden = false;
    }

    desc.parentNode.replaceChild(out, desc);
    if (inclEl) { inclEl.id = 'wnx-included'; }

    renderDownloads(root, downloads);
    wireNav(root, out, inclEl);
  }

  function renderDownloads(root, downloads) {
    var sec = root.querySelector('#wnx-downloads');
    var grid = sec && sec.querySelector('.wnx-dlgrid');
    if (!grid) return;
    var seen = {}, cards = [];
    downloads.forEach(function (a) {
      var href = a.getAttribute('href');
      if (!href || seen[href]) return;
      seen[href] = 1;
      var label = a.textContent.trim().replace(/[:\s]+$/, '') || 'Product document';
      if (label.length > 70) label = label.slice(0, 68).trim() + '…';
      cards.push('<a class="wnx-dl" href="' + href + '" target="_blank" rel="noopener">' +
        '<span class="wnx-dl-ico">PDF</span><span class="wnx-dl-txt">' +
        '<span class="wnx-dl-t">' + label + '</span>' +
        '<span class="wnx-dl-m">Opens in a new tab</span></span>' +
        '<span class="wnx-dl-a">' + DOWN + '</span></a>');
    });
    if (!cards.length) return;
    grid.innerHTML = cards.join('');
    sec.hidden = false;
    var link = root.querySelector('.wnx-subnav a[href="#wnx-downloads"]');
    if (link) link.hidden = false;
  }

  function wireNav(root, content, inclEl) {
    var nav = root.querySelector('.wnx-subnav-in');
    if (!nav) return;

    /* What's included */
    if (inclEl) {
      var a = document.createElement('a');
      a.href = '#wnx-included';
      a.textContent = "What's included";
      nav.appendChild(a);
    }

    /* Reviews: the theme renders these in its own section further down */
    var rev = null;
    Array.prototype.forEach.call(document.querySelectorAll('.shopify-section'), function (s) {
      if (rev || s.contains(root) || root.contains(s)) return;
      var h = s.querySelector('h1,h2,h3');
      var headed = h && /review|customers say|testimonial/i.test(h.textContent || '');
      var widget = s.querySelector('[class*="trustpilot"],[class*="tv-elevate"],[class*="loox-reviews"]');
      if ((headed || widget) && s.getBoundingClientRect().height > 60) rev = s;
    });
    if (rev) {
      if (!rev.id) rev.id = 'wnx-reviews';
      rev.style.scrollMarginTop = '64px';
      var r = document.createElement('a');
      r.href = '#' + rev.id;
      r.textContent = 'Reviews';
      nav.appendChild(r);
    }

    /* order tabs to match where things actually sit on the page */
    Array.prototype.slice.call(nav.querySelectorAll('a')).map(function (link) {
      var t = document.querySelector(link.getAttribute('href'));
      return { a: link, top: t ? t.getBoundingClientRect().top + window.scrollY : Number.MAX_SAFE_INTEGER };
    }).sort(function (x, y) { return x.top - y.top; })
      .forEach(function (o) { nav.appendChild(o.a); });

    var links = Array.prototype.slice.call(nav.querySelectorAll('a')).filter(function (l) { return !l.hidden; });
    if (!links.length || !('IntersectionObserver' in window)) return;
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        links.forEach(function (l) { l.classList.toggle('on', l.getAttribute('href') === '#' + en.target.id); });
      });
    }, { rootMargin: '-110px 0px -72% 0px' });
    links.forEach(function (l) {
      var t = document.querySelector(l.getAttribute('href'));
      if (t) obs.observe(t);
    });
  }


  /* ---------- image lightbox ---------- */
  function lightbox(root) {
    var stage = root.querySelector('.wnx-stage');
    if (!stage || stage.dataset.lb) return;
    stage.dataset.lb = '1';

    var thumbs = Array.prototype.slice.call(root.querySelectorAll('.wnx-thumb'));
    var srcs = thumbs.length
      ? thumbs.map(function (t) { return t.dataset.src; })
      : (function () { var i = stage.querySelector('img'); return i ? [i.src] : []; })();
    if (!srcs.length) return;

    var idx = 0;
    var box, imgEl, counter, lastFocus;

    function myStageImg() {
      /* target our own <img id="wnxStage-..."> — ignore badge-app images (BSS labels etc.) that inject into the stage */
      return stage.querySelector('img[id^="wnxStage-"]') || (function(){
        var imgs = stage.querySelectorAll('img');
        for (var k=0; k<imgs.length; k++){
          if (!/bss-pl|bss_pl|badge|label/i.test(imgs[k].className)) return imgs[k];
        }
        return imgs[0] || null;
      })();
    }
    function currentFromStage() {
      var cur = myStageImg();
      if (!cur) return 0;
      /* prefer the index tracked by the thumb-swap code (immune to URL mismatches) */
      if (cur.dataset && typeof cur.dataset.i !== 'undefined' && cur.dataset.i !== '') {
        var t = parseInt(cur.dataset.i, 10);
        if (!isNaN(t) && t >= 0 && t < srcs.length) return t;
      }
      /* fallbacks: exact URL match, then filename-only match */
      var s = cur.getAttribute('src') || '';
      var i = srcs.indexOf(s);
      if (i >= 0) return i;
      var fname = s.split('?')[0].split('/').pop();
      for (var j = 0; j < srcs.length; j++) {
        if (srcs[j].split('?')[0].split('/').pop() === fname) return j;
      }
      return 0;
    }

    function render() {
      imgEl.src = srcs[idx];
      if (counter) counter.textContent = (idx + 1) + ' / ' + srcs.length;
    }

    function show(n) {
      idx = (n + srcs.length) % srcs.length;
      render();
    }

    function build() {
      box = document.createElement('div');
      box.className = 'wnx-lb';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', 'Product image viewer');
      box.innerHTML =
        '<button type="button" class="wnx-lb-close" aria-label="Close image viewer">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
        (srcs.length > 1 ?
          '<button type="button" class="wnx-lb-prev" aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M15 5l-7 7 7 7"/></svg></button>' +
          '<button type="button" class="wnx-lb-next" aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M9 5l7 7-7 7"/></svg></button>' : '') +
        '<figure class="wnx-lb-fig"><img alt=""></figure>' +
        (srcs.length > 1 ? '<div class="wnx-lb-count"></div>' : '');
      document.body.appendChild(box);
      imgEl = box.querySelector('img');
      counter = box.querySelector('.wnx-lb-count');

      box.querySelector('.wnx-lb-close').addEventListener('click', close);
      var p = box.querySelector('.wnx-lb-prev'), n = box.querySelector('.wnx-lb-next');
      if (p) p.addEventListener('click', function (e) { e.stopPropagation(); show(idx - 1); });
      if (n) n.addEventListener('click', function (e) { e.stopPropagation(); show(idx + 1); });
      box.addEventListener('click', function (e) { if (e.target === box || e.target.closest('.wnx-lb-fig')) close(); });

      /* swipe on touch */
      var x0 = null;
      box.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, {passive:true});
      box.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 45) show(idx + (dx < 0 ? 1 : -1));
        x0 = null;
      }, {passive:true});
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(idx - 1);
      else if (e.key === 'ArrowRight') show(idx + 1);
    }

    function open() {
      lastFocus = document.activeElement;
      if (!box) build();
      show(currentFromStage());
      box.classList.add('on');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      box.querySelector('.wnx-lb-close').focus();
    }

    function close() {
      if (!box) return;
      box.classList.remove('on');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    stage.addEventListener('click', open);
    stage.setAttribute('role', 'button');
    stage.setAttribute('tabindex', '0');
    stage.setAttribute('aria-label', 'Open larger image');
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });
  }

  ready(function () {
    document.querySelectorAll('.wnx-pdp').forEach(function (root) {
      build(root);
      lightbox(root);
    });
  });
})();
